import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Settings as SettingsIcon, Save, Upload, X, ArrowUp, ArrowDown, ListOrdered,
  StickyNote, Trash2, Plus,
} from "lucide-react";
import { toast } from "sonner";

// مسمّيات عربية واضحة لعرضها فوق كل حقل قابل لإعادة التسمية — بدل
// عرض المفتاح الداخلي الخام (ministry_name) كما هو، غير مفهوم للمستخدم.
const FIELD_LABEL_CAPTIONS = {
  ministry_name: "مسمّى حقل: اسم الوزارة",
  education_dept: "مسمّى حقل: إدارة التعليم",
  embassy_name: "مسمّى حقل: السفارة",
  office_name: "مسمّى حقل: مكتب التعليم",
  school_name: "مسمّى حقل: اسم المدرسة",
  school_manager: "مسمّى حقل: مدير المدرسة",
  egypt_supervisor: "مسمّى حقل: مشرف المسار المصري",
};

const DOC_TEXT_CAPTIONS = {
  cert_title: "عنوان الشهادة",
  reviewer1: "المراجع الأول",
  reviewer2: "المراجع الثاني",
  control_committee_head: "رئيس لجنة النظام والمراقبة",
  performance_certification: "مصادقة الأداء المعرفي",
};

export default function Settings() {
  const [form, setForm] = useState({
    school_name: "", ministry_name: "وزارة التعليم",
    education_dept: "", office_name: "", embassy_name: "",
    school_manager: "", egypt_supervisor: "",
    school_logo: "", ministry_logo: "",
    field_labels: {}, document_texts: {},
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm(data)).catch(() => {});
  }, []);

  const save = async () => {
    setLoading(true);
    try { await api.put("/settings", form); toast.success("تم حفظ الإعدادات"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLabel = (k, v) => setForm((f) => ({ ...f, field_labels: { ...f.field_labels, [k]: v } }));
  const setText = (k, v) => setForm((f) => ({ ...f, document_texts: { ...f.document_texts, [k]: v } }));

  /** يحوّل ملف شعار مرفوع إلى نص base64 ويخزّنه مباشرة في الإعدادات —
   * لا حاجة لتخزين ملفات منفصل، الشعارات صغيرة الحجم عادةً بما يكفي. */
  const handleLogoUpload = (key, file) => {
    if (!file) return;
    if (file.size > 1_500_000) {
      toast.error("حجم الصورة كبير جدًا — الرجاء اختيار شعار أصغر من 1.5 ميجابايت");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set(key, reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHeader
        icon={SettingsIcon}
        overline="الإعدادات"
        title="إعدادات المدرسة"
        subtitle="بيانات المدرسة، الشعارات، ومسمّيات الحقول التي تظهر في الشهادات."
        actions={
          <Button onClick={save} disabled={loading} className="h-10 bg-white text-[#0E7469] hover:bg-[#F4D06F] hover:text-[#083F38] font-semibold" data-testid="settings-save">
            <Save className="h-4 w-4 me-1.5" /> {loading ? "جارٍ الحفظ…" : "حفظ التغييرات"}
          </Button>
        }
      />

      <Tabs defaultValue="school" className="rounded-xl border border-border bg-white p-4">
        <TabsList className="mb-4">
          <TabsTrigger value="school" data-testid="tab-school">بيانات المدرسة</TabsTrigger>
          <TabsTrigger value="labels" data-testid="tab-labels">تسميات الحقول</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">نصوص الشهادات</TabsTrigger>
          <TabsTrigger value="subject-order" data-testid="tab-subject-order">ترتيب المواد</TabsTrigger>
          <TabsTrigger value="cert-notes" data-testid="tab-cert-notes">ملاحظات الشهادة</TabsTrigger>
        </TabsList>

        <TabsContent value="school">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <LogoUploader
              label="شعار المدرسة"
              value={form.school_logo}
              onChange={(file) => handleLogoUpload("school_logo", file)}
              onClear={() => set("school_logo", "")}
              testId="settings-school-logo"
            />
            <LogoUploader
              label="شعار الوزارة"
              value={form.ministry_logo}
              onChange={(file) => handleLogoUpload("ministry_logo", file)}
              onClear={() => set("ministry_logo", "")}
              testId="settings-ministry-logo"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <F label="اسم الوزارة" value={form.ministry_name} onChange={(v) => set("ministry_name", v)} />
            <F label="إدارة التعليم" value={form.education_dept} onChange={(v) => set("education_dept", v)} />
            <F label="مكتب التعليم" value={form.office_name} onChange={(v) => set("office_name", v)} />
            <F label="السفارة" value={form.embassy_name} onChange={(v) => set("embassy_name", v)} />
            <F label="اسم المدرسة" value={form.school_name} onChange={(v) => set("school_name", v)} testId="settings-school-name" />
            <F label="مدير المدرسة" value={form.school_manager} onChange={(v) => set("school_manager", v)} />
            <F label="مشرف المسار المصري" value={form.egypt_supervisor} onChange={(v) => set("egypt_supervisor", v)} />
          </div>
        </TabsContent>

        <TabsContent value="labels">
          <p className="text-sm text-muted-foreground mb-3">
            هذه التسميات تظهر داخل الشهادات — يمكن تعديلها لتتناسب مع تنظيم مدرستك
            (مثال: تغيير "مشرف المسار المصري" إلى "منسق المسار المصري").
          </p>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(form.field_labels).map(([k, v]) => (
              <F key={k} label={FIELD_LABEL_CAPTIONS[k] || k} value={v} onChange={(nv) => setLabel(k, nv)} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="docs">
          <p className="text-sm text-muted-foreground mb-3">
            نصوص الشهادات الثابتة (العنوان، أسماء المراجعين، رئيس اللجنة).
          </p>
          <div className="grid grid-cols-1 gap-4">
            {Object.entries(form.document_texts).map(([k, v]) => (
              <F key={k} label={DOC_TEXT_CAPTIONS[k] || k} value={v} onChange={(nv) => setText(k, nv)} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="subject-order">
          <SubjectOrderTab />
        </TabsContent>

        <TabsContent value="cert-notes">
          <CertificateNotesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function F({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px]">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="h-10" />
    </div>
  );
}

function LogoUploader({ label, value, onChange, onClear, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px]">{label}</Label>
      <div className="flex items-center gap-3 rounded-lg border border-dashed border-border p-3">
        {value ? (
          <img src={value} alt={label} className="h-14 w-14 rounded-md object-contain border border-border bg-white" />
        ) : (
          <div className="h-14 w-14 rounded-md border border-border bg-muted flex items-center justify-center text-[10px] text-muted-foreground">
            بلا شعار
          </div>
        )}
        <label className="flex-1">
          <input
            type="file" accept="image/png,image/jpeg,image/svg+xml"
            className="hidden" data-testid={testId}
            onChange={(e) => onChange(e.target.files?.[0])}
          />
          <span className="inline-flex items-center gap-1.5 text-sm text-primary cursor-pointer hover:underline">
            <Upload className="h-4 w-4" /> {value ? "تغيير الصورة" : "رفع صورة"}
          </span>
        </label>
        {value && (
          <button type="button" onClick={onClear} className="text-muted-foreground hover:text-destructive">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/** تبويب "ترتيب المواد" — ترتيب يدوي (أسهم لأعلى/لأسفل) لظهور المواد في
 * الشهادة، محفوظ لكل (مرحلة + سنة + صف). المادة/المواد "تضاف للمجموع"
 * والتي "لا تضاف للمجموع" تُعرَض في مجموعتين منفصلتين بصريًا هنا فقط
 * للتوضيح — الترتيب المحفوظ فعليًا هو ترتيب واحد متصل لكل المواد؛ موضع
 * "المجموع" نفسه بين المجموعتين يُحسَب تلقائيًا عند بناء الشهادة (ليس
 * جزءًا من هذا الترتيب القابل للتعديل، ولا يمكن للمستخدم نقله). */
function SubjectOrderTab() {
  const { ctx, meta } = useApp();
  const grades = meta?.grades_by_stage?.[ctx.stage] || [];
  const [grade, setGrade] = useState("");
  const [subjects, setSubjects] = useState([]); // [{name, add_to_total}]
  const [order, setOrder] = useState([]); // [name, ...] — الترتيب المحفوظ/المعدَّل حاليًا
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!grade) { setSubjects([]); setOrder([]); return; }
    setLoading(true);
    Promise.all([
      api.get("/subjects", { params: { stage: ctx.stage, year: ctx.year, grade, semester: 1 } }),
      api.get("/subjects", { params: { stage: ctx.stage, year: ctx.year, grade, semester: 2 } }),
      api.get("/subject-order", { params: { stage: ctx.stage, year: ctx.year, grade } }),
    ]).then(([r1, r2, ro]) => {
      const byName = {};
      [...r1.data, ...r2.data].forEach((s) => {
        if (!byName[s.subject_name]) byName[s.subject_name] = { name: s.subject_name, add_to_total: s.add_to_total !== false };
      });
      const names = Object.keys(byName);
      const saved = (ro.data.order || []).filter((n) => names.includes(n));
      const extra = names.filter((n) => !saved.includes(n)).sort();
      setSubjects(Object.values(byName));
      setOrder([...saved, ...extra]);
    }).catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [ctx.stage, ctx.year, grade]);

  const byName = Object.fromEntries(subjects.map((s) => [s.name, s]));
  const totalGroup = order.filter((n) => byName[n]?.add_to_total);
  const nonTotalGroup = order.filter((n) => byName[n] && !byName[n]?.add_to_total);

  const move = (group, name, dir) => {
    const idx = group.indexOf(name);
    const swapWith = idx + dir;
    if (swapWith < 0 || swapWith >= group.length) return;
    const newGroup = [...group];
    [newGroup[idx], newGroup[swapWith]] = [newGroup[swapWith], newGroup[idx]];
    // إعادة تجميع الترتيب الكامل: نفس مجموعة "تضاف للمجموع" أولًا (بترتيبها
    // الجديد)، ثم مجموعة "لا تضاف للمجموع" (بترتيبها كما هو أو الجديد).
    const isTotalGroup = byName[name]?.add_to_total;
    setOrder(isTotalGroup ? [...newGroup, ...nonTotalGroup] : [...totalGroup, ...newGroup]);
  };

  const save = async () => {
    if (!grade) return;
    setSaving(true);
    try {
      await api.put("/subject-order", { stage: ctx.stage, year: ctx.year, grade, order });
      toast.success("تم حفظ ترتيب المواد");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        حدِّد ترتيب ظهور المواد في الشهادة (الفردية والجماعية) وملف مراجعة الرصد — بدل الاعتماد على ترتيب إضافتها في شاشة المواد الدراسية.
        استخدم الأسهم لتحريك أي مادة لأعلى أو لأسفل.
      </p>

      <div className="grid grid-cols-2 gap-4 max-w-xl">
        <div className="space-y-1.5">
          <Label className="text-[12px]">الصف</Label>
          <Select value={grade} onValueChange={setGrade}>
            <SelectTrigger className="h-10" data-testid="subject-order-grade"><SelectValue placeholder="اختر الصف" /></SelectTrigger>
            <SelectContent>
              {grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!grade ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
          اختر الصف لعرض مواده وترتيبها
        </div>
      ) : loading ? (
        <div className="text-center text-muted-foreground text-sm py-10">جارٍ التحميل…</div>
      ) : subjects.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
          لا توجد مواد مضافة لهذا الصف بعد
        </div>
      ) : (
        <>
          <OrderGroup
            title="المواد التي تُضاف للمجموع"
            hint="تظهر أولًا في الشهادة، ويظهر صف «المجموع» تلقائيًا بعدها مباشرة (للإعدادي والثانوي فقط)."
            names={totalGroup}
            onMove={(name, dir) => move(totalGroup, name, dir)}
          />
          <OrderGroup
            title="المواد التي لا تُضاف للمجموع"
            hint="تظهر بعد صف «المجموع» مباشرة."
            names={nonTotalGroup}
            onMove={(name, dir) => move(nonTotalGroup, name, dir)}
          />
          <Button onClick={save} disabled={saving} className="h-10" data-testid="subject-order-save">
            <Save className="h-4 w-4 me-1.5" /> {saving ? "جارٍ الحفظ…" : "حفظ ترتيب المواد"}
          </Button>
        </>
      )}
    </div>
  );
}

function OrderGroup({ title, hint, names, onMove }) {
  if (!names.length) return null;
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-border">
        <div className="text-sm font-semibold flex items-center gap-1.5"><ListOrdered className="h-4 w-4 text-primary" /> {title}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>
      </div>
      <div className="divide-y divide-slate-100">
        {names.map((name, i) => (
          <div key={name} className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-[11px] font-bold">{i + 1}</span>
              <span className="text-sm">{name}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0}
                       onClick={() => onMove(name, -1)} data-testid={`order-up-${name}`}>
                <ArrowUp className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === names.length - 1}
                       onClick={() => onMove(name, 1)} data-testid={`order-down-${name}`}>
                <ArrowDown className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** تبويب "ملاحظات الشهادة" — نص حر يكتبه المستخدم، يحدِّد لأي صفوف (عبر
 * كل المراحل) ولأي قسم (الكل/عربي/لغات) يظهر، مع إمكانية إضافة عدد غير
 * محدود من الملحوظات المستقلة. تُطبَّق تلقائيًا على الشهادة الفردية
 * والجماعية، كل الفصول الدراسية، بلا أي كود مبرمَج مسبقًا — الشرط بالكامل
 * محفوظ في قاعدة البيانات ويُطابَق من الـ Backend عند توليد كل شهادة. */
function CertificateNotesTab() {
  const { ctx, meta } = useApp();
  // مرتبط بالمرحلة الحالية التي يعمل عليها المستخدم (ctx.stage) — وليس كل
  // المراحل معًا — حتى تُعرَض صفوف مرحلته فقط في نموذج الإضافة.
  const gradesInCurrentStage = meta?.grades_by_stage?.[ctx.stage] || [];

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [section, setSection] = useState("الكل");
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/certificate-notes").then(({ data }) => setNotes(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // عند تبديل المرحلة من أعلى الصفحة، امسح أي صفوف مُحدَّدة من المرحلة
  // السابقة — حتى لا تُرسَل صفوف غير ظاهرة في القائمة الحالية بالخطأ.
  useEffect(() => { setSelectedGrades([]); }, [ctx.stage]);

  const toggleGrade = (g) => {
    setSelectedGrades((prev) => prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]);
  };

  const reset = () => { setText(""); setSection("الكل"); setSelectedGrades([]); };

  const add = async () => {
    if (!text.trim()) return toast.error("اكتب نص الملحوظة أولًا");
    if (!selectedGrades.length) return toast.error("اختر صفًا واحدًا على الأقل");
    setSaving(true);
    try {
      await api.post("/certificate-notes", { text: text.trim(), grades: selectedGrades, section });
      toast.success("تمت إضافة الملحوظة");
      reset();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف هذه الملحوظة نهائيًا؟")) return;
    try {
      await api.delete(`/certificate-notes/${id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        أضِف نصوصًا مخصَّصة (ملاحظات) تظهر تلقائيًا في الشهادة — فردية أو جماعية، أي فصل دراسي — بعد جدول درجات الطالب مباشرة،
        حسب الصفوف والقسم اللذين تحدِّدهما. يمكنك إضافة أكثر من ملحوظة لصفوف/أقسام مختلفة.
      </p>

      {/* نموذج إضافة ملحوظة جديدة */}
      <div className="rounded-lg border border-border p-4 space-y-4">
        <div className="space-y-1.5">
          <Label className="text-[12px]">نص الملحوظة</Label>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
                     placeholder="مثال: ملحوظة: الطالب أدى الاختبارات باللغة الإنجليزية"
                     data-testid="cert-note-text" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5 md:col-span-1">
            <Label className="text-[12px]">تظهر لقسم</Label>
            <Select value={section} onValueChange={setSection}>
              <SelectTrigger className="h-10" data-testid="cert-note-section"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="الكل">كل الأقسام</SelectItem>
                <SelectItem value="عربي">عربي فقط</SelectItem>
                <SelectItem value="لغات">لغات فقط</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-[12px]">
            تظهر للصفوف التالية (اختر واحدًا أو أكثر) — صفوف "{ctx.stage}" الحالية
          </Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 rounded-md border border-border p-3">
            {gradesInCurrentStage.map((g) => (
              <label key={g} className="flex items-center gap-2 text-[13px] cursor-pointer">
                <Checkbox checked={selectedGrades.includes(g)} onCheckedChange={() => toggleGrade(g)}
                           data-testid={`cert-note-grade-${g}`} />
                {g}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">
            لإضافة ملحوظة لصفوف مرحلة أخرى، بدِّل المرحلة من أعلى الصفحة ثم عُد لهذا التبويب.
          </p>
        </div>

        <Button onClick={add} disabled={saving} className="h-10" data-testid="cert-note-add">
          <Plus className="h-4 w-4 me-1.5" /> {saving ? "جارٍ الإضافة…" : "إضافة ملحوظة"}
        </Button>
      </div>

      {/* الملحوظات الحالية */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
          <StickyNote className="h-4 w-4 text-primary" /> الملاحظات الحالية
        </h3>
        {loading ? (
          <div className="text-center text-muted-foreground text-sm py-8">جارٍ التحميل…</div>
        ) : notes.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
            لا توجد ملاحظات مضافة بعد
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((n) => (
              <div key={n.id} className="rounded-lg border border-border p-3 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{n.text}</div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                      {n.section === "الكل" ? "كل الأقسام" : n.section}
                    </span>
                    {(n.grades || []).map((g) => (
                      <span key={g} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{g}</span>
                    ))}
                  </div>
                </div>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                         onClick={() => remove(n.id)} data-testid={`cert-note-delete-${n.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
